/**
 * Promotional price display.
 *
 * Mirrors the native Shopify automatic discount on the product page — Liquid
 * cannot read automatic discounts, so the figures come from the product's promo
 * metafields — and pushes the same figures into the Recharge one-time purchase
 * option, which the widget renders after this script runs.
 */
(function () {
  var ONE_TIME_PRICE_SELECTOR = '.rc-option__onetime .rc_widget__price--onetime';

  var root = document.querySelector('[data-promo-price]');

  if (!root) {
    return;
  }

  var config = {
    type: root.getAttribute('data-promo-type'),
    value: parseFloat(root.getAttribute('data-promo-value')),
    original: parseInt(root.getAttribute('data-promo-original'), 10),
    current: parseInt(root.getAttribute('data-promo-current'), 10)
  };

  /**
   * Promo rate per variant id, resolved server side in promo-price.liquid.
   *
   * The fields live on the variant, so the rate is not a property of the
   * product: two variants can be discounted differently, and one can be on
   * promo while its sibling is not. A variant missing from this map has no
   * promo and must render at full price.
   */
  var variantPromos = {};

  try {
    variantPromos = JSON.parse(root.getAttribute('data-promo-variants') || '{}');
  } catch (e) {
    // Malformed map: fall back to the rate the page rendered with rather than
    // leaving the price frozen on the first variant.
    variantPromos = {};
  }

  function formatMoney(cents) {
    if (window.theme && theme.Currency && theme.Currency.formatMoney) {
      return theme.Currency.formatMoney(cents, theme.moneyFormat);
    }

    return '$' + (cents / 100).toFixed(2);
  }

  /**
   * Same arithmetic as snippets/promo-config.liquid, so a variant change cannot
   * disagree with what the page rendered. Liquid's `divided_by` truncates, so
   * floor here too.
   */
  function promoPriceFor(cents) {
    var savings = 0;

    // promo-config normalises the metafield to 'percentage' or 'fixed_amount'
    // before it reaches the map, so anything else here is a fixed amount by the
    // same rule, never a silently skipped discount.
    if (config.type === 'percentage') {
      savings = Math.floor((cents * config.value) / 100);
    } else if (config.type) {
      savings = Math.round(config.value * 100);
    }

    if (savings > cents) {
      savings = cents;
    }

    return cents - savings;
  }

  /**
   * Whether the selected variant is actually discounted.
   *
   * config.type is empty and current equals original whenever the variant has
   * no promo, both on first render from the Liquid data attributes and after a
   * variant change. Either alone would do; requiring both keeps a zero-value
   * promo from striking a price through at the same figure.
   */
  function hasPromo() {
    return !!config.type && config.current < config.original;
  }

  function priceMarkup() {
    // Nothing to strike through when the variant is not discounted, so emit the
    // price on its own. Returning both spans here was putting a line through
    // the Recharge one-time row at its own undiscounted price.
    if (!hasPromo()) {
      return (
        '<span class="promo-current-price">' +
        formatMoney(config.current) +
        '</span>'
      );
    }

    return (
      '<span class="promo-original-price">' +
      formatMoney(config.original) +
      '</span>' +
      '<span class="promo-current-price">' +
      formatMoney(config.current) +
      '</span>'
    );
  }

  function renderPrice() {
    var originalPrice = root.querySelector('[data-promo-original-price]');
    var currentPrice = root.querySelector('[data-promo-current-price]');

    if (originalPrice) {
      originalPrice.textContent = formatMoney(config.original);
    }

    if (currentPrice) {
      currentPrice.textContent = formatMoney(config.current);
    }
  }

  function renderSavings() {
    var labels = document.querySelectorAll('[data-promo-savings]');
    var text;

    if (config.type === 'percentage') {
      // The rate is per variant, so a percentage no longer reads the same for
      // every variant and has to be re-rendered like a fixed amount does.
      // Liquid drops a trailing zero on a whole number, so match that here.
      text = (Math.round(config.value) === config.value
        ? Math.round(config.value)
        : config.value) + '%';
    } else if (config.type) {
      text = formatMoney(config.original - config.current);
    } else {
      return;
    }

    for (var i = 0; i < labels.length; i++) {
      // The label is authored as e.g. "Save [savings]" and Liquid passes that
      // template through verbatim, so substitute into it rather than trying to
      // recover the wording from whatever is currently on screen. A variant
      // with no promo renders the label empty, which leaves nothing to recover.
      var label = labels[i];
      var template = label.getAttribute('data-promo-savings-template');

      if (!template) {
        // Older markup without the attribute: fall back to deriving it once
        // from the rendered text, which still holds for a label that was
        // rendered with a figure in it.
        template = label.textContent.replace(/\S*%|\$[\d,.]+/, '[savings]');
        label.setAttribute('data-promo-savings-template', template);
      }

      label.textContent = template.replace('[savings]', text);
    }
  }

  function renderCopy(promo) {
    var blocks = document.querySelectorAll('[data-promo-copy]');

    for (var i = 0; i < blocks.length; i++) {
      var block = blocks[i];

      // Show the line whenever the variant has a promo. Blank copy still
      // shows the savings label on its own.
      if (promo) {
        block.classList.remove('promo-copy--inactive');
      } else {
        block.classList.add('promo-copy--inactive');
      }

      // Always overwrite, so a variant with blank copy clears the previous
      // variant's wording instead of keeping it on screen.
      var textNode = block.querySelector('[data-promo-copy-text]');

      if (textNode) {
        textNode.textContent = (promo && promo.copy) || '';
      }
    }
  }

  function renderRechargeOneTime() {
    var oneTimePrice = document.querySelector(ONE_TIME_PRICE_SELECTOR);

    if (!oneTimePrice) {
      return;
    }

    var markup = priceMarkup();

    if (oneTimePrice.innerHTML !== markup) {
      oneTimePrice.innerHTML = markup;
    }
  }

  function render() {
    renderPrice();
    renderSavings();
    renderRechargeOneTime();
  }

  function onVariantChange(evt) {
    var variant = evt.detail && evt.detail.variant;

    if (!variant || typeof variant.price !== 'number') {
      return;
    }

    // Adopt the incoming variant's own rate before pricing it. Without this the
    // first variant's discount would be applied to every other variant.
    var promo = variantPromos[variant.id];

    config.type = promo ? promo.type : null;
    config.value = promo ? promo.value : 0;
    config.original = variant.price;
    config.current = promo ? promoPriceFor(variant.price) : variant.price;

    root.setAttribute('data-promo-type', config.type || '');
    root.setAttribute('data-promo-value', config.value);
    root.setAttribute('data-promo-original', config.original);
    root.setAttribute('data-promo-current', config.current);

    // A variant with no promo has nothing to strike through and nothing to
    // save, so drop the promo chrome rather than showing the same figure twice.
    // Two-argument classList.toggle is unsupported in IE11, which this theme
    // still shims for, so add and remove explicitly.
    if (promo) {
      root.classList.remove('promo-price-display--inactive');
    } else {
      root.classList.add('promo-price-display--inactive');
    }

    renderCopy(promo);
    render();
  }

  function init() {
    render();

    // Recharge injects its widget late and re-renders it on selection, so keep
    // watching rather than rendering once.
    new MutationObserver(renderRechargeOneTime).observe(document.body, {
      childList: true,
      subtree: true
    });

    document.addEventListener('variantChange', onVariantChange);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
